import { useEffect, useRef, useState } from "react";

/**
 * Like `useState` but mirrors the value to `sessionStorage` under `key` so
 * it survives a full page refresh. Use for filters / dropdown selections
 * that should NOT reset when the user reloads the tab.
 *
 * Notes:
 *   - Storage is keyed by `key`. Pick a stable, unique string per view
 *     (e.g. "associateFee:columnFilters").
 *   - JSON-serializable values only. `Set` is supported automatically.
 *   - When `key` changes, the hook re-reads from storage and updates state.
 *   - Pass `defaultValue` either as a value or a lazy initializer function.
 */
export default function usePersistedState(key, defaultValue) {
  const initialDefault = useRef(defaultValue);
  const [value, setValue] = useState(() => readFromStorage(key, defaultValue));

  // If the storage `key` itself changes (e.g. user picks a different
  // month/clinic and we want a separate persisted bucket), re-hydrate.
  useEffect(() => {
    setValue(readFromStorage(key, initialDefault.current));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);

  // Persist on every change.
  useEffect(() => {
    if (!key) return;
    try {
      sessionStorage.setItem(key, JSON.stringify(serialize(value)));
    } catch {
      // ignore quota / serialization errors
    }
  }, [key, value]);

  return [value, setValue];
}

function readFromStorage(key, fallback) {
  const fb = typeof fallback === "function" ? fallback() : fallback;
  if (!key) return fb;
  try {
    const raw = sessionStorage.getItem(key);
    if (raw == null) return fb;
    return deserialize(JSON.parse(raw), fb);
  } catch {
    return fb;
  }
}

// Serialize Sets as { __set: [...] } so they round-trip through JSON.
// Recurses into plain objects/arrays so nested Sets (e.g. a `Record<string, Set<string>>`
// filter map) are preserved.
function serialize(v) {
  if (v instanceof Set) return { __set: [...v] };
  if (Array.isArray(v)) return v.map(serialize);
  if (v && typeof v === "object") {
    const out = {};
    for (const [k, val] of Object.entries(v)) out[k] = serialize(val);
    return out;
  }
  return v;
}

function deserialize(parsed, fallback) {
  const restored = restoreSets(parsed);
  // Match the shape of fallback when possible (e.g. plain object filters).
  if (
    fallback &&
    typeof fallback === "object" &&
    !Array.isArray(fallback) &&
    !(fallback instanceof Set) &&
    restored &&
    typeof restored === "object"
  ) {
    return restored;
  }
  return restored;
}

function restoreSets(parsed) {
  if (
    parsed &&
    typeof parsed === "object" &&
    !Array.isArray(parsed) &&
    Array.isArray(parsed.__set)
  ) {
    return new Set(parsed.__set);
  }
  if (Array.isArray(parsed)) return parsed.map(restoreSets);
  if (parsed && typeof parsed === "object") {
    const out = {};
    for (const [k, val] of Object.entries(parsed)) out[k] = restoreSets(val);
    return out;
  }
  return parsed;
}
